import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import type RTKClient from "@cloudflare/realtimekit";
import type { ChatUpdateParams, Message, TextMessage } from "@cloudflare/realtimekit";

function isTextMessage(message: Message): message is TextMessage {
  return message.type === "text";
}

export function getLivestreamChatText(message: Message): string | null {
  if (!isTextMessage(message)) return null;
  return message.message.trim() || null;
}

export function useLivestreamChat({
  meeting,
}: {
  meeting: RTKClient | undefined;
}) {
  const [messages, setMessages] = useState<Message[]>([]);

  useEffect(() => {
    if (!meeting?.chat) return;

    setMessages([...meeting.chat.messages]);

    const onChatUpdate = (payload: ChatUpdateParams) => {
      if (payload.action === "add") {
        setMessages((prev) => [...prev, payload.message]);
        return;
      }
      if (payload.action === "edit") {
        setMessages((prev) =>
          prev.map((m) => (m.id === payload.message.id ? payload.message : m)),
        );
        return;
      }
      if (payload.action === "delete") {
        setMessages((prev) => prev.filter((m) => m.id !== payload.message.id));
      }
    };

    meeting.chat.on("chatUpdate", onChatUpdate);
    return () => {
      meeting.chat.off("chatUpdate", onChatUpdate);
    };
  }, [meeting]);

  const sendMessage = useCallback(
    async ({ text }: { text: string }) => {
      const trimmed = text.trim();
      if (!meeting?.chat || !trimmed) return;
      await meeting.chat.sendTextMessage(trimmed);
    },
    [meeting],
  );

  return { messages, sendMessage };
}

export function useLivestreamChatUnreadCount({
  messages,
  isChatVisible,
}: {
  messages: Message[];
  isChatVisible: boolean;
}) {
  const textMessageCount = messages.filter(
    (message) => getLivestreamChatText(message) != null,
  ).length;
  const [readUpToCount, setReadUpToCount] = useState(textMessageCount);

  useLayoutEffect(() => {
    if (isChatVisible) {
      setReadUpToCount(textMessageCount);
    }
  }, [isChatVisible, textMessageCount]);

  if (isChatVisible) return 0;
  return Math.max(0, textMessageCount - readUpToCount);
}
